import { RefreshCw, CreditCard, Clock, AlertCircle } from 'lucide-react';
import { SEOHead } from '@/components/seo';
import { PAGE_SEO } from '@/lib/seo-config';

export default function Refund() {
  return (
    <div className="bg-[#050505] min-h-screen pt-24">
      <SEOHead
        title={PAGE_SEO.refund.title}
        description={PAGE_SEO.refund.description}
        keywords={PAGE_SEO.refund.keywords}
        url="/legal/refund"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Legal', url: '/legal/terms' },
          { name: 'Refund Policy', url: '/legal/refund' },
        ]}
      />
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 data-testid="refund-title" className="text-4xl font-bold text-white mb-4 section-marker font-mono">Refund & Cancellation Policy</h1>
          <p className="text-xs font-mono text-zinc-500 mb-12">Last updated: February 1, 2026</p>

          {/* Quick Info Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
            {[
              { icon: RefreshCw, title: 'Refund Window', desc: '7 days from purchase', color: 'text-emerald-400' },
              { icon: CreditCard, title: 'Processing Time', desc: '5-7 business days', color: 'text-cyan-400' },
              { icon: Clock, title: 'Cancellation', desc: 'Anytime, no penalties', color: 'text-emerald-400' },
              { icon: AlertCircle, title: 'Credits', desc: 'Non-refundable once used', color: 'text-amber-400' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 flex items-start gap-4">
                  <Icon size={20} className={item.color} />
                  <div>
                    <h3 className="text-sm font-mono text-white font-bold">{item.title}</h3>
                    <p className="text-xs text-zinc-500">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-8">
            {[
              { 
                title: '1. Subscription Cancellation', 
                body: 'You may cancel your subscription at any time through your dashboard Settings. Cancellation takes effect at the end of your current billing period. You will retain access to paid features until that date. No partial refunds are issued for unused days within a billing period.' 
              },
              { 
                title: '2. Refund Eligibility', 
                body: 'We offer a full refund within 7 days of your initial subscription purchase if you are not satisfied with the Service. To be eligible, you must not have deployed more than 2 instances or used more than $10 in compute credits during this period.' 
              },
              { 
                title: '3. Credit Purchases', 
                body: 'Purchased credits are non-refundable once added to your account. Unused credits remain in your account indefinitely and do not expire. If your account is terminated for violation of terms, unused credits will be forfeited.' 
              },
              { 
                title: '4. How to Request a Refund', 
                body: 'To request a refund, email billing@clawstinger.com with your account email and order ID within the 7-day window. Include the reason for your refund request. We aim to process all requests within 3 business days.' 
              },
              { 
                title: '5. Refund Processing', 
                body: 'Approved refunds are processed to the original payment method. Credit card refunds typically appear within 5-7 business days depending on your bank. Cryptocurrency payments will be refunded in the equivalent USD value at current rates.' 
              },
              { 
                title: '6. Annual Subscriptions', 
                body: 'Annual subscriptions may be refunded on a prorated basis within the first 30 days. After 30 days, annual subscriptions are non-refundable but may be cancelled to prevent renewal. Contact support for annual plan cancellations.' 
              },
              { 
                title: '7. Service Disruptions', 
                body: 'If ClawStinger experiences downtime exceeding our 99.9% SLA guarantee, affected users will receive service credits. Extended outages (>24 hours) may qualify for partial refunds at our discretion.' 
              },
              { 
                title: '8. Chargebacks', 
                body: 'Filing a chargeback without first contacting our support team may result in account suspension. We encourage you to reach out to resolve any billing disputes directly.' 
              },
              { 
                title: '9. Contact for Billing Issues', 
                body: 'For any billing questions or refund requests, contact billing@clawstinger.com or use the Support page in your dashboard. Response time is typically within 24 hours.' 
              },
            ].map((s, i) => (
              <div key={i}>
                <h2 className="text-lg font-mono text-white font-bold mb-2">{s.title}</h2>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
